<template>
  <div class="min-h-screen bg-off-black text-white">
    <div class="container mx-auto px-4 py-16 max-w-4xl">
      <!-- Header Section -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-serif font-semibold mb-6 text-vibe-green">
          {{ videoTitle }}
        </h1>
        <p class="text-xl md:text-2xl text-gray-green">
          {{ videoDescription }}
        </p>
      </div>

      <!-- Video Player (shown when password is correct) -->
      <div v-if="videoUnlocked" class="mb-12">
        <div class="aspect-video bg-green-700 rounded-lg overflow-hidden shadow-2xl border border-vibe-green">
          <iframe
            :src="vimeoEmbedUrl"
            width="100%"
            height="100%"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          ></iframe>
        </div>
        <div class="mt-6 text-center">
          <p class="text-gray-green text-sm">
            This video is for viewing purposes only. Please do not share the access password or attempt to download this content.
          </p>
        </div>
      </div>

      <!-- Step 1: Email Collection (shown first) -->
      <div v-if="!emailSubmitted && !videoUnlocked" class="bg-green-700 rounded-lg p-8 md:p-12 shadow-2xl border border-green-600 max-w-md mx-auto">
        <h2 class="text-2xl font-semibold mb-4 text-vibe-green text-center">Access Video</h2>
        <p class="text-gray-green mb-6 text-center">Please enter your details to access this video.</p>

        <!-- Email Form -->
        <div class="space-y-4">
          <div>
            <label for="userName" class="block text-sm font-semibold mb-2 text-vibe-green">
              Name
            </label>
            <input
              type="text"
              id="userName"
              v-model="userName"
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Your name"
            />
          </div>

          <div>
            <label for="userEmail" class="block text-sm font-semibold mb-2 text-vibe-green">
              Email Address
            </label>
            <input
              type="email"
              id="userEmail"
              v-model="userEmail"
              @keyup.enter="submitEmail"
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="your@email.com"
              required
            />
          </div>

          <!-- Error Message -->
          <div v-if="emailError" class="bg-error-red bg-opacity-20 border border-error-red rounded-lg p-3">
            <p class="text-error-red text-sm">{{ emailError }}</p>
          </div>

          <button
            @click="submitEmail"
            class="btn-primary w-full text-lg font-semibold py-3"
          >
            Continue
          </button>
        </div>
      </div>

      <!-- Step 2: Password Entry (shown after email) -->
      <div v-if="emailSubmitted && !videoUnlocked" class="bg-green-700 rounded-lg p-8 md:p-12 shadow-2xl border border-green-600 max-w-md mx-auto">
        <h2 class="text-2xl font-semibold mb-4 text-vibe-green text-center">Protected Video</h2>
        <p class="text-gray-green mb-6 text-center">Please enter the password to access this video.</p>

        <!-- Password Input -->
        <div class="space-y-4">
          <div>
            <label for="videoPassword" class="block text-sm font-semibold mb-2 text-vibe-green">
              Password
            </label>
            <input
              type="password"
              id="videoPassword"
              v-model="passwordInput"
              @keyup.enter="checkPassword"
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter password"
            />
          </div>

          <!-- Error Message for Wrong Password -->
          <div v-if="passwordError" class="bg-error-red bg-opacity-20 border border-error-red rounded-lg p-3">
            <p class="text-error-red text-sm">{{ passwordError }}</p>
          </div>

          <button
            @click="checkPassword"
            class="btn-primary w-full text-lg font-semibold py-3"
          >
            Unlock Video
          </button>

          <button
            @click="goBackToEmail"
            class="text-vibe-green hover:text-white transition-colors text-sm w-full text-center mt-2"
          >
            ← Change email
          </button>

          <p class="text-xs text-gray-green text-center mt-4">
            Contact us if you need assistance accessing this video.
          </p>
        </div>
      </div>

      <!-- Back to Home Link -->
      <div class="text-center mt-8">
        <router-link to="/" class="text-vibe-green hover:text-white transition-colors">
          ← Back to Resources
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VideoAccessPage',
  props: {
    videoTitle: {
      type: String,
      required: true,
    },
    videoDescription: {
      type: String,
      required: true,
    },
    vimeoVideoId: {
      type: String,
      required: true,
    },
    videoType: {
      type: String,
      required: true, // 'acsp-training' or 'marker-demo'
    },
  },
  data() {
    return {
      // Step 1: Email collection
      userName: '',
      userEmail: '',
      emailError: '',
      emailSubmitted: false,
      // Step 2: Password
      passwordInput: '',
      passwordError: '',
      // Step 3: Video access
      videoUnlocked: false,
    };
  },
  computed: {
    vimeoEmbedUrl() {
      // Vimeo embed URL with privacy settings
      // The video should be set to "Hide from Vimeo.com" in Vimeo settings
      return `https://player.vimeo.com/video/${this.vimeoVideoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
    },
  },
  methods: {
    submitEmail() {
      this.emailError = '';

      // Validate email
      if (!this.userEmail) {
        this.emailError = 'Please enter your email address.';
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.userEmail)) {
        this.emailError = 'Please enter a valid email address.';
        return;
      }

      // Move to password step
      this.emailSubmitted = true;
    },

    goBackToEmail() {
      this.emailSubmitted = false;
      this.passwordInput = '';
      this.passwordError = '';
    },

    async checkPassword() {
      this.passwordError = '';

      if (!this.passwordInput) {
        this.passwordError = 'Please enter a password.';
        return;
      }

      try {
        // Verify password with backend and log access
        const response = await axios.post('/api/verify-video-password', {
          password: this.passwordInput,
          videoType: this.videoType,
          // Include user details for tracking
          userName: this.userName,
          userEmail: this.userEmail,
        });

        if (response.data.success) {
          this.videoUnlocked = true;
          // Scroll to video
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        } else {
          this.passwordError = 'Incorrect password. Please try again or contact us for assistance.';
        }
      } catch (error) {
        console.error('Password verification error:', error);
        this.passwordError = 'Error verifying password. Please try again.';
      }
    },
  },
};
</script>

<style scoped>
.aspect-video {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
}
</style>
